import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useReactToPrint } from "react-to-print";
import { useEffect, useRef, useState } from "react";
import { FaPlus } from "react-icons/fa6";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import ContentPage from "@/components/contentPage";
import QrCode from "@/components/qr";
import RegisterManifestForm from "@/forms/registerManifest";
import useApiClient from "@/hooks/useApiClient";
import { JobStatus } from "@/apiClient";
import { poll } from "@/apiUtils";
import ManifestCard from "@/components/cards/manifestCard";

const uploadManifest = async ({ api, name, dataVolumeId, data }: { api: any; name: string; dataVolumeId: string; data: ArrayBuffer }) => {
  const {
    upload_url: uploadUrl,
    import_job_public_id: jobPublicId,
    upload_headers: uploadHeaders,
  } = await api.dataTransfer.createManifest({ name, dataVolumeId });

  // Upload to blob storage
  const headers = {
    "Content-Type": "application/json",
    ...uploadHeaders,
  };
  await fetch(uploadUrl, { method: "PUT", headers: headers, body: data });

  // Check for metadata import job completion every 2 seconds
  const importError = Error(
    JSON.stringify({ detail: "Unable to import manifest" })
  );

  const checkJobStatus = () => api.job.getJobStatus({ jobPublicId });
  const isJobInProgress = ({ status }: { status: string }) => {
    if (status === JobStatus.SUCCESS) {
      return false;
    } else if (status === JobStatus.FAILURE) {
      throw importError;
    }
    return true;
  };

  try {
    await poll(checkJobStatus, isJobInProgress, 2000);
  } catch (e) {
    throw importError;
  }
};

const volumeDetailQueryOptions = (api: any, publicId: string) =>
  queryOptions({
    queryKey: ["getDataVolume", publicId],
    queryFn: () => api.dataTransfer.dataVolumeDetail({ publicId }),
    enabled: !!publicId,
  });

export const Route = createFileRoute("/data/volumes/$publicId/")({
  component: DataVolumeDetail,
  loader: ({ context: { queryClient, api }, params: { publicId } }) =>
    queryClient.prefetchQuery(volumeDetailQueryOptions(api, publicId)),
});

function DataVolumeDetail() {
  const componentRef = useRef();
  const navigate = useNavigate();
  const api = useApiClient();
  const { publicId } = Route.useParams();

  const routerLocation = useRouterState({ select: (s) => s.location });

  const [fullPath, updateFullPath] = useState("");

  const [addingNewManifest, setAddingNewManifest] = useState(false);

  useEffect(() => {
    updateFullPath(window.location.origin + routerLocation.pathname + routerLocation.searchStr);
  }, [routerLocation]);

  const { isPending, error, data, refetch } = useQuery(
    volumeDetailQueryOptions(api, publicId),
  );

  const handleUploadNewManifest = async (formData: any) => {
    let { manifest, name } = formData[0];
    let data = await manifest[0].arrayBuffer();
    await toast.promise(
      () =>
        uploadManifest({
          api,
          data,
          name,
          dataVolumeId: publicId,
        }),
      {
        pending: "Uploading manifest",
        error: { render: ({ data }: { data: any }) => `${data.message}` },
        success: "Successfully uploaded manifest",
      }
    );
    setAddingNewManifest(false);
    return refetch();
  };

  const handleDeleteManifest = async (publicId: string) => {
    await toast.promise(() => api.dataTransfer.deleteManifest({ publicId }), {
      pending: "Deleting manifest",
      error: { render: ({ data }: { data: any }) => `${data.message}` },
      success: "Successfully deleted manifest",
    });
    return refetch();
  };

  const manifestNames = data && data.manifests.map((ms: any) => ms.name);
  const QrCodeAny = QrCode as any;
  const qr = (
    <QrCodeAny
      path={fullPath}
      title={data?.name}
      subtitles={manifestNames}
      ref={componentRef}
    />
  );
  const handlePrint = useReactToPrint({
    contentRef: componentRef as any,
  });

  if (isPending) return "Loading...";
  if (error) return "An error has occurred: " + error.message;

  return (
    <ContentPage title={`Data Volume: ${data?.name}`}>
      <div className="hidden">{qr}</div>
      <div className="mb-8 flex h-8 flex-row gap-4">
        <button
          className="ox-button flex-grow text-center"
          onClick={handlePrint}
        >
          Print QR Code
        </button>
        <button
          className="ox-button"
          onClick={() => setAddingNewManifest(true)}
          disabled={addingNewManifest}
        >
          <FaPlus />
          <span className="pl-4 align-middle">Add manifest</span>
        </button>
        <button
          onClick={async () => {
            await toast.promise(
              () => api.dataTransfer.deleteDataVolume({ publicId }),
              {
                pending: "Deleting data volume",
                error: { render: ({ data }: { data: any }) => `${data.message}` },
                success: "Successfully deleted data volume",
              }
            );
            await navigate({ to: "/data/volumes" });
          }}
          className="ox-red-button text-center"
        >
          Delete
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {data &&
          data.manifests.map((manifest: any) => (
            <ManifestCard
              key={manifest.public_id}
              manifest={manifest}
              handleDeleteManifest={handleDeleteManifest}
            />
          ))}
        {addingNewManifest && (
          <div className="relative rounded-lg border border-gray-300 p-4 shadow-md">
            <RegisterManifestForm
              onSubmit={handleUploadNewManifest}
              handleClose={() => setAddingNewManifest(false)}
            />
          </div>
        )}
      </div>
    </ContentPage>
  );
}
